import './style.scss';

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';

import logo from '../../../assets/images/logo.png';
import { getUserData, ISOBSERVER, ISSUPPORTER } from '../../../constants/global';
import { pageurl } from '../../../constants/pageurl';
import { useGlobalContext } from '../../../context/global';
import { useTheme } from '../../../context/theme-context';
import type { IActions } from '../../../interface/IAction';
import { CopyValue } from '../../utils/copy';
import LocationTracker from '../../utils/location-tracker';
import type { IRightSection } from '../../utils/right-section';
import { HamburgerSVG } from '../../utils/svgs';
import ThemeToggle from '../../utils/theme-toggle';

const Header = ({ actions, rsProps }: { actions: IActions; rsProps: IRightSection<{}> }) => {
	const navigate = useNavigate();
	const { themeMode, setThemeMode } = useTheme();
	const { locationTracking } = useGlobalContext();
	const { pathname } = useLocation();
	// The observer home prints the tracking status on the pass itself.
	const onObserverHome = ISOBSERVER() && pathname === pageurl.OVERVIEW;
	// Profile and Settings are an agent's (volunteer profile, bank details, government ID).
	const isSupporter = ISSUPPORTER();
	const [isOpen, setIsOpen] = useState(false);
	const dropdownRef = useRef<HTMLDivElement>(null);

	const onOpenMenu = () => {
		rsProps.callSection({
			action: 'view',
			component: 'side-menu-mobile',
			title: 'Menu',
		});
	};

	const userData = getUserData();
	const userName = userData?.user?.UserName || '';
	const fullName = userData?.user?.FullName || userName;
	const email = userData?.user?.Email || '';
	const role = userData?.user?.role || '';
	const initials = fullName
		? fullName
				.split(' ')
				.map((n: string) => n[0])
				.join('')
				.slice(0, 2)
		: '';

	const handleClickOutside = useCallback((e: MouseEvent) => {
		if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
			setIsOpen(false);
		}
	}, []);

	useEffect(() => {
		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, [handleClickOutside]);

	const handleNavigate = (path: string) => {
		setIsOpen(false);
		navigate(path);
	};

	return (
		<div className='header-wrapper'>
			<div className='f-row-60 w-100 justify-content-between align-items-center'>
				<div className='f-row-15 nav-brand align-items-center after-lg'>
					<img
						src={logo}
						alt='iElect'
						className='header-logo'
						onClick={() => navigate(pageurl.OVERVIEW)}
					/>
				</div>
				<div className='f-row-10 align-items-center ml-auto after-lg'>
					<ThemeToggle compact />
					<div
						className='hw-mx cursor-pointer'
						onClick={onOpenMenu}
					>
						<HamburgerSVG />
					</div>
				</div>
				<div className='f-row-20 align-items-center ml-auto justify-content-end only-lg'>
					{locationTracking && locationTracking.status !== 'idle' && !onObserverHome && (
						<LocationTracker
							variant='header'
							tracking={locationTracking}
						/>
					)}
					<div
						className='header-profile-menu'
						ref={dropdownRef}
					>
						<div
							className='header-avatar-pill'
							onClick={() => setIsOpen(!isOpen)}
						>
							<div className='avatar-circle'>{initials}</div>
							<span className='avatar-name'>{fullName}</span>
							<svg
								className={`avatar-chevron ${isOpen ? 'avatar-chevron--open' : ''}`}
								width='12'
								height='12'
								viewBox='0 0 24 24'
								fill='none'
								stroke='currentColor'
								strokeWidth='2.5'
								strokeLinecap='round'
								strokeLinejoin='round'
							>
								<polyline points='6 9 12 15 18 9' />
							</svg>
						</div>
						{isOpen && (
							<div className='profile-dropdown'>
								<div className='profile-dropdown__header'>
									<div className='profile-dropdown__avatar'>{initials}</div>
									<div className='profile-dropdown__info'>
										<p className='profile-dropdown__name'>{fullName}</p>
										<p className='profile-dropdown__email'>
											{email}
											<CopyValue
												value={email}
												label='email'
												size={14}
												stopPropagation
											/>
										</p>
										{role && <span className='profile-dropdown__role'>{role}</span>}
									</div>
								</div>
								<div className='profile-dropdown__divider' />
								{isSupporter ? null : (
									<>
										<button
											type='button'
											className='profile-dropdown__item'
											onClick={() => handleNavigate(pageurl.SETTINGSPROFILE)}
										>
											<svg
												width='16'
												height='16'
												viewBox='0 0 24 24'
												fill='none'
												stroke='currentColor'
												strokeWidth='2'
												strokeLinecap='round'
												strokeLinejoin='round'
											>
												<path d='M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2' />
												<circle
													cx='12'
													cy='7'
													r='4'
												/>
											</svg>
											View Profile
										</button>
										<button
											type='button'
											className='profile-dropdown__item'
											onClick={() => handleNavigate(pageurl.SETTINGS)}
										>
											<svg
												width='16'
												height='16'
												viewBox='0 0 24 24'
												fill='none'
												stroke='currentColor'
												strokeWidth='2'
												strokeLinecap='round'
												strokeLinejoin='round'
											>
												<circle
													cx='12'
													cy='12'
													r='3'
												/>
												<path d='M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z' />
											</svg>
											Settings
										</button>
									</>
								)}
								<div className='profile-dropdown__theme'>
									<svg
										width='16'
										height='16'
										viewBox='0 0 24 24'
										fill='none'
										stroke='currentColor'
										strokeWidth='2'
										strokeLinecap='round'
										strokeLinejoin='round'
									>
										<circle
											cx='12'
											cy='12'
											r='5'
										/>
										<line
											x1='12'
											y1='1'
											x2='12'
											y2='3'
										/>
										<line
											x1='12'
											y1='21'
											x2='12'
											y2='23'
										/>
										<line
											x1='4.22'
											y1='4.22'
											x2='5.64'
											y2='5.64'
										/>
										<line
											x1='18.36'
											y1='18.36'
											x2='19.78'
											y2='19.78'
										/>
										<line
											x1='1'
											y1='12'
											x2='3'
											y2='12'
										/>
										<line
											x1='21'
											y1='12'
											x2='23'
											y2='12'
										/>
										<line
											x1='4.22'
											y1='19.78'
											x2='5.64'
											y2='18.36'
										/>
										<line
											x1='18.36'
											y1='5.64'
											x2='19.78'
											y2='4.22'
										/>
									</svg>
									<span>Theme</span>
									<div className='profile-dropdown__theme-btns'>
										<button
											type='button'
											className={themeMode === 'light' ? 'active' : ''}
											onClick={() => setThemeMode('light')}
										>
											Light
										</button>
										<button
											type='button'
											className={themeMode === 'system' ? 'active' : ''}
											onClick={() => setThemeMode('system')}
										>
											Auto
										</button>
										<button
											type='button'
											className={themeMode === 'dark' ? 'active' : ''}
											onClick={() => setThemeMode('dark')}
										>
											Dark
										</button>
									</div>
								</div>
								<div className='profile-dropdown__divider' />
								<button
									type='button'
									className='profile-dropdown__item profile-dropdown__item--danger'
									onClick={() => {
										setIsOpen(false);
										actions.logout_Auth({});
									}}
								>
									<svg
										width='16'
										height='16'
										viewBox='0 0 24 24'
										fill='none'
										stroke='currentColor'
										strokeWidth='2'
										strokeLinecap='round'
										strokeLinejoin='round'
									>
										<path d='M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4' />
										<polyline points='16 17 21 12 16 7' />
										<line
											x1='21'
											y1='12'
											x2='9'
											y2='12'
										/>
									</svg>
									Log out
								</button>
							</div>
						)}
					</div>
				</div>
			</div>
		</div>
	);
};

export default Header;
